// @ts-strict-ignore
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { closeModal, pushModal } from 'loot-core/client/actions';
import { send } from 'loot-core/platform/client/fetch';
import { updateTransaction } from 'loot-core/shared/transactions';
import {
  amountToInteger,
  integerToAmount,
} from 'loot-core/src/shared/util';
import { type TransactionEntity } from 'loot-core/types/models';

import { useAccounts } from '../../hooks/useAccounts';
import { useCategories } from '../../hooks/useCategories';
import { usePayees } from '../../hooks/usePayees';
import { useDispatch } from '../../redux';
import { Button } from '../ui/button2';
import { InlineField } from '../common/InlineField';
import { Input } from '../ui/input2';
import {
  Modal,
  ModalButtons,
  ModalCloseButton,
  ModalHeader,
  ModalTitle,
} from '../ui/modal';
import { Select } from '../ui/select2';
import { Text } from '../common/Text';
import { View } from '../common/View';

type EditTransactionModalProps = {
  transaction: TransactionEntity;
};

export function EditTransactionModal({
  transaction,
}: EditTransactionModalProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const accounts = useAccounts();
  const { list: categories } = useCategories();
  const payees = usePayees();

  const [date, setDate] = useState(transaction.date || '');
  const [accountId, setAccountId] = useState<string>(transaction.account);
  const [payeeId, setPayeeId] = useState<string | undefined>(
    transaction.payee,
  );
  const [notes, setNotes] = useState(transaction.notes || '');
  const [categoryId, setCategoryId] = useState<string | undefined>(
    transaction.category,
  );
  const [amount, setAmount] = useState(
    String(integerToAmount(transaction.amount)),
  );
  const [cleared, setCleared] = useState<boolean>(
    transaction.cleared ?? false,
  );

  const payeeName =
    payees?.find(p => p.id === payeeId)?.name ?? t('None');
  const categoryName =
    categories?.find(c => c.id === categoryId)?.name ?? t('None');
  const accountName =
    accounts?.find(a => a.id === accountId)?.name ?? t('Unknown');

  const onSave = async () => {
    const updatedAmount = amountToInteger(parseFloat(amount) || 0);
    const updatedTransaction: TransactionEntity = {
      ...transaction,
      date,
      account: accountId,
      payee: payeeId,
      notes,
      category: categoryId,
      amount: updatedAmount,
      cleared,
      ...(accountId !== transaction.account && { reconciled: false }),
    };

    const { diff } = updateTransaction([transaction], updatedTransaction);
    await send('transactions-batch-update', diff);
    dispatch(closeModal());
  };

  return (
    <Modal name="edit-transaction">
      {({ state: { close } }) => (
        <>
          <ModalHeader
            title={
              <ModalTitle title={t('Edit Transaction')} shrinkOnOverflow />
            }
            rightContent={<ModalCloseButton onClick={close} />}
          />
          <View>
            <InlineField label={t('Date')} width="100%">
              <Input
                value={date}
                onChange={e => setDate(e.target.value)}
                style={{ flex: 1 }}
                placeholder="YYYY-MM-DD"
              />
            </InlineField>

            <InlineField label={t('Account')} width="100%">
              <View
                style={{
                  flex: 1,
                  flexDirection: 'row',
                  alignItems: 'center',
                }}
              >
                <Text className="text-[var(--color-tableText)]" style={{ flex: 1 }}>
                  {accountName}
                </Text>
                <Button
                  variant="bare"
                  onClick={() =>
                    dispatch(
                      pushModal('account-autocomplete', {
                        onSelect: (id: string) => setAccountId(id),
                      }),
                    )
                  }
                >
                  <Trans>Change</Trans>
                </Button>
              </View>
            </InlineField>

            <InlineField label={t('Payee')} width="100%">
              <View
                style={{
                  flex: 1,
                  flexDirection: 'row',
                  alignItems: 'center',
                }}
              >
                <Text className="text-[var(--color-tableText)]" style={{ flex: 1 }}>
                  {payeeName}
                </Text>
                <Button
                  variant="bare"
                  onClick={() =>
                    dispatch(
                      pushModal('payee-autocomplete', {
                        onSelect: (id: string) => setPayeeId(id),
                      }),
                    )
                  }
                >
                  <Trans>Change</Trans>
                </Button>
              </View>
            </InlineField>

            <InlineField label={t('Notes')} width="100%">
              <Input
                value={notes}
                onChange={e => setNotes(e.target.value)}
                style={{ flex: 1 }}
              />
            </InlineField>

            <InlineField label={t('Category')} width="100%">
              <View
                style={{
                  flex: 1,
                  flexDirection: 'row',
                  alignItems: 'center',
                }}
              >
                <Text className="text-[var(--color-tableText)]" style={{ flex: 1 }}>
                  {categoryName}
                </Text>
                <Button
                  variant="bare"
                  onClick={() =>
                    dispatch(
                      pushModal('category-autocomplete', {
                        onSelect: (id: string) => setCategoryId(id),
                      }),
                    )
                  }
                >
                  <Trans>Change</Trans>
                </Button>
              </View>
            </InlineField>

            <InlineField label={t('Amount')} width="100%">
              <Input
                value={amount}
                onChange={e => setAmount(e.target.value)}
                style={{ flex: 1 }}
              />
            </InlineField>

            <InlineField label={t('Cleared')} width="100%">
              <Select
                value={cleared ? 'true' : 'false'}
                onChange={v => setCleared(v === 'true')}
                options={[
                  ['true', t('Cleared')],
                  ['false', t('Not cleared')],
                ]}
                style={{ flex: 1 }}
              />
            </InlineField>

            <ModalButtons>
              <Button onClick={close}>
                <Trans>Cancel</Trans>
              </Button>
              <Button
                variant="primary"
                onClick={onSave}
                style={{ marginLeft: 10 }}
              >
                <Trans>Save</Trans>
              </Button>
            </ModalButtons>
          </View>
        </>
      )}
    </Modal>
  );
}
